'use client';

import { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { hasCookieProperty, CampaignVoucher, type LocaleIso, COOKIE_NAMES } from 'utils';
import type { VoucherUnionNode, Device, Constant } from 'data-layer';
import { DownloadVoucherCampaign } from './partials/DownloadVoucherCampaign';
import { DownloadVoucherCampaignDialog } from './partials/DownloadVoucherCampaignDialog';
import { DownloadVoucherCampaignSkeleton } from './partials/DownloadVoucherCampaignSkeleton';

interface DownloadVoucherCampaignWrapperProps {
  vouchers: VoucherUnionNode[];
  locale: LocaleIso;
  device: Device;
  constants: Constant;
}

/**
 * DownloadVoucherCampaignWrapperContent - Internal component that uses useSearchParams
 *
 * Responsibilities:
 * - Read campaign params from URL (searchParams) client-side
 * - Use CampaignVoucher to select correct voucher variant
 * - Calculate emailId for PSI service
 * - Handle cookie-based dialog visibility
 *
 * This component runs client-side to avoid blocking static generation.
 * Server provides ALL vouchers, client selects the right one based on URL params.
 */
function DownloadVoucherCampaignWrapperContent({
  vouchers,
  locale,
  device,
  constants,
}: DownloadVoucherCampaignWrapperProps) {
  const searchParams = useSearchParams();

  // Read campaign parameters from URL
  const prefParam = searchParams.get('p_ref');
  const campaignIdParam = searchParams.get('campaignID');

  const campaignParams = {
    pref: prefParam || undefined,
    campaignId: campaignIdParam ? parseInt(campaignIdParam, 10) : undefined,
  };

  // Use CampaignVoucher to select variant and get campaign details
  const campaignVoucher = new CampaignVoucher(locale);
  const selectedVoucher = campaignVoucher.selectVoucherVariant(vouchers, campaignParams) || vouchers[0];
  const campaignDetails = campaignVoucher.getCampaignDetails(campaignParams);

  // Check cookie client-side (won't block static generation)
  // Only show dialog if cookie value is NOT 'true'
  const dialogShown = hasCookieProperty(COOKIE_NAMES.DOWNLOAD_DIALOG_SHOWN, 'true');

  // Common props for both campaign components
  const campaignProps = {
    voucher: selectedVoucher,
    device,
    constants,
    variant: campaignDetails.variant,
    emailId: campaignDetails.emailId,
  };

  return (
    <>
      {/* Regular voucher display on page */}
      <DownloadVoucherCampaign {...campaignProps} />

      {/* Dialog - shows 2s after load if not shown before */}
      {!dialogShown && <DownloadVoucherCampaignDialog {...campaignProps} />}
    </>
  );
}

/**
 * DownloadVoucherCampaignWrapper - Client component with Suspense boundary
 *
 * Wraps the content component with Suspense to handle async searchParams.
 * Provides clean loading state while searchParams resolve.
 */
export function DownloadVoucherCampaignWrapper(props: DownloadVoucherCampaignWrapperProps) {
  return (
    <Suspense fallback={<DownloadVoucherCampaignSkeleton />}>
      <DownloadVoucherCampaignWrapperContent {...props} />
    </Suspense>
  );
}
